/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed } from "mobx";
import { appIdentifiers, hudUiControlAlignments } from "constants/enums";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { HUD_ANIMATION_OUT_DURATION, HUD_ANIMATION_IN_DURATION } from "constants/constants";
import { setComponentProperty, updateComponentProperties } from "utils/APIUtils";
import { overlayEvents } from "overlays/OverlayEvents";
import { hudHideManager } from "utils/ComponentHideManager";
import { isZoomFeatureEnabled, getShouldUseModernViewer } from "selectors/appSelectors";
import { contextMenuManager } from "contextMenuManager";
import { getItemsForOverFlowMenu, hudDisabled, getIsHUDDocked, shouldAutoHideHud } from "selectors/hudSelectors";
import { isBoolean } from "utils/lodashUtils";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import * as stores from "stores";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isSinglePageMode } from "selectors/documentSelectors";
import { isNotANegativeNumber } from "utils/common";

const setVisibility = (obj, key, value) => {
  if (isBoolean(value)) {
    obj[key] = value;
  }
};

class HUDStore {
  @observable hudHandle;
  @observable isDisabled;
  @observable applyAnimationClasses;
  @observable isDocked;
  // tracks if mouse pointer is over the hud.
  // setting initially to true so that first time we see hud
  // and then it fades out
  @observable isMouseOver;
  @observable isAnimationOut;
  @observable shouldAutoHide;
  @observable isFocused;
  @observable showZoomUI;
  @observable left;
  @observable dockedWidth;
  @observable hudWidth;
  @observable shouldUpdateHUDPosition;
  @observable animationType;
  @observable animationInDuration;
  @observable animationOutDuration;
  @observable componentProperties;
  @observable externalComponentsIds;
  @observable enableRotationInModernViewer;
  @observable isExplicitlyDisabled;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
    this.initComponentProperties();
  }

  @action
  init() {
    this.hudHandle = null;
    this.isDisabled = false;
    this.applyAnimationClasses = false;
    this.isDocked = true;
    this.isMouseOver = true;
    this.isAnimationOut = false;
    this.shouldAutoHide = false;
    this.isFocused = false;
    this.showZoomUI = true;
    this.left = 0;
    this.dockedWidth = 0;
    this.hudWidth = 0;
    this.shouldUpdateHUDPosition = true;
    this.animationType = "FADE";
    this.animationInDuration = HUD_ANIMATION_IN_DURATION;
    this.animationOutDuration = HUD_ANIMATION_OUT_DURATION;
    this.componentProperties = new Map();
    this.externalComponentsIds = [];
    this.enableRotationInModernViewer = false;

    // non-observables
    this.externalComponents = new Map();
    this.nextExternalComponentId = 0;
    this.animationInOutTimer = null;
    this.isExplicitlyDisabled = false;
  }

  initComponentProperties() {
    this.componentProperties.set(appIdentifiers.FIT_PAGE, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.FIT_WIDTH, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.FIXED_WIDTH, {
      visible: false
    });
    this.componentProperties.set(appIdentifiers.PAGE_NAVIGATION, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.PAGE_NUMBER, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.ZOOM_CONTROL, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.PRINT, {
      visible: false
    });
    this.componentProperties.set(appIdentifiers.DOWNLOAD, {
      visible: false
    });
    this.componentProperties.set(appIdentifiers.DOCK, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.TWO_COLUMN, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.ROTATE_VIEW_CONTROL, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.PAGE_VIEW_SWITCHER, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.FULL_SCREEN, {
      visible: true
    });
    this.componentProperties.set(appIdentifiers.VIEW_WITH_SCROLL, {
      visible: true
    });
  }

  @computed
  get getLeftAlignedExternalComponents() {
    const leftAlignedComponents = this.externalComponentsIds.reduce((accumulator, currentValue) => {
      if (this.externalComponents.get(currentValue).alignment === hudUiControlAlignments.LEFT) {
        accumulator.push(this.externalComponents.get(currentValue));
      }
      return accumulator;
    }, []);
    return leftAlignedComponents;
  }

  @computed
  get getRightAlignedExternalComponents() {
    const rightAlignedComponents = this.externalComponentsIds.reduce((accumulator, currentValue) => {
      if (this.externalComponents.get(currentValue).alignment === hudUiControlAlignments.RIGHT) {
        accumulator.push(this.externalComponents.get(currentValue));
      }
      return accumulator;
    }, []);
    return rightAlignedComponents;
  }

  @computed
  get getCenterAlignedExternalComponents() {
    const centerAlignedComponents = this.externalComponentsIds.reduce((accumulator, currentValue) => {
      if (this.externalComponents.get(currentValue).alignment === hudUiControlAlignments.CENTER) {
        accumulator.push(this.externalComponents.get(currentValue));
      }
      return accumulator;
    }, []);
    return centerAlignedComponents;
  }

  @action.bound
  subscribeExternalComponentsForOverFlowMenu() {
    contextMenuManager.subscribe({
      name: "overflow_menu",
      contextMenuHandler: () => {
        const itemList = getItemsForOverFlowMenu(this.stores);
        return Promise.resolve(itemList);
      },
      type: contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK
    });
  }

  @action.bound
  unsubscribeExternalComponentsForOverFlowMenu() {
    contextMenuManager.unsubscribe(contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK);
  }

  isAddUIControlArgumentsValid(component, alignment) {
    if (
      alignment !== hudUiControlAlignments.RIGHT &&
      alignment !== hudUiControlAlignments.LEFT &&
      alignment !== hudUiControlAlignments.CENTER
    )
      return false;
    if (component && typeof component === "object") {
      let isIconRequired = false;
      if (getShouldUseModernViewer(this.stores) || alignment === hudUiControlAlignments.LEFT) {
        isIconRequired = true;
      }
      if (!(component.title && component.clickHandler && (isIconRequired ? component.icon : true))) {
        return false;
      }
    } else if (typeof component !== "function") {
      return false;
    }
    return true;
  }

  @action.bound
  addExternalComponent(component, alignment = hudUiControlAlignments.RIGHT) {
    if (this.isAddUIControlArgumentsValid(component, alignment)) {
      const componentId = ++this.nextExternalComponentId;
      this.externalComponents.set(componentId, {
        component,
        alignment
      });
      this.externalComponentsIds.push(componentId);
      return componentId;
    }
    return null;
  }

  @action.bound
  removeComponent(id) {
    let index = this.externalComponentsIds.indexOf(id);
    if (index !== -1) {
      this.externalComponentsIds.splice(index, 1);
      this.externalComponents.delete(id);
    }
  }

  resetExternalComponents(alignment = hudUiControlAlignments.RIGHT) {
    this.externalComponents.forEach((component, id) => {
      if (component.alignment === alignment) {
        this.removeComponent(id);
      }
    });
  }

  @action.bound
  setHudHandle(hudHandle) {
    this.hudHandle = hudHandle;
  }

  @action.bound
  show(animationType, duration) {
    if (isNotANegativeNumber(duration) && getIsHUDDocked(stores) && shouldAutoHideHud(stores)) {
      this.animationType = animationType;
      this.resetAnimationInOutTimer();
      const id = setTimeout(() => {
        this.setApplyAnimationClasses(false);
      }, duration);
      this.setAnimationInOutTimer(id);
      this.setAnimationInDuration(duration);
      this.setApplyAnimationClasses(true);
      this.setHudDisable(false);
      this.setShouldAutoHide(false);
      this.setIsAnimationOut(false);
      return true;
    }
    return false;
  }
  @action.bound
  hide(animationType, duration) {
    if (isNotANegativeNumber(duration) && getIsHUDDocked(stores) && !hudDisabled(stores)) {
      this.animationType = animationType;
      this.resetAnimationInOutTimer();
      const id = setTimeout(() => {
        this.setHudDisable(true);
        this.setIsAnimationOut(true);
        this.setApplyAnimationClasses(false);
      }, duration);
      this.setAnimationInOutTimer(id);
      this.setAnimationOutDuration(duration);
      this.setApplyAnimationClasses(true);
      this.setShouldAutoHide(true);
      return true;
    }
    return false;
  }
  getHudExplicitlyDisabled() {
    return this.isExplicitlyDisabled;
  }

  @action.bound
  setHudExplicitlyDisabled(value) {
    this.isExplicitlyDisabled = value;
  }

  @action.bound
  setApplyAnimationClasses(value) {
    this.applyAnimationClasses = value;
  }

  @action.bound
  setIsAnimationOut(value) {
    this.isAnimationOut = value;
  }

  @action.bound
  setAnimationOutDuration(value) {
    this.animationOutDuration = value;
  }

  @action.bound
  setAnimationInDuration(value) {
    this.animationInDuration = value;
  }

  @action.bound
  setAnimationInOutTimer(timeoutId) {
    this.animationInOutTimer = timeoutId;
  }

  @action.bound
  resetAnimationInOutTimer() {
    window.clearTimeout(this.animationInOutTimer);
    this.animationInOutTimer = null;
  }

  @action.bound
  setHudDisable(isDisabled = false) {
    this.isDisabled = isDisabled;
    if (!isDisabled && !this.isDocked) {
      hudHideManager.resurfaceHud(this.stores);
    }
  }

  shouldSetHudDockStatus() {
    // do not set dock status of HUD for mobile and tablet devices for single page and in place mode since because HUD is always docked in these cases
    if (getIsInPlaceMode(stores) || isSinglePageMode(stores)) {
      return true;
    }
    if (!(isCurrentDeviceMobile() || isCurrentDeviceTablet())) return true;

    return false;
  }

  @action
  setIsDocked(isDocked = false) {
    if (this.isDocked !== isDocked && this.shouldSetHudDockStatus()) {
      this.isDocked = isDocked;
      if (!isDocked) {
        this.setApplyAnimationClasses(false);
        this.setShouldAutoHide(false);
        this.resetAnimationInOutTimer();
        hudHideManager.resurfaceHud(this.stores);
      }
      overlayEvents.trigger("hudDockingStatusChanged", {
        isDocked
      });
    }
  }

  @action.bound
  setMouseOver(isMouseOver) {
    this.isMouseOver = isMouseOver;
  }

  @action.bound
  setShouldUpdateHUDPosition(value) {
    this.shouldUpdateHUDPosition = value;
  }

  @action.bound
  setShouldAutoHide(shouldAutoHide) {
    this.shouldAutoHide = shouldAutoHide;
  }

  @action.bound
  setFocus(isFocused) {
    this.isFocused = isFocused;
  }

  @action.bound
  setZoomUIVisibility(visible) {
    setVisibility(this, "showZoomUI", visible);
  }

  @action.bound
  setPageNavigationVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.PAGE_NAVIGATION, "visible", visible);
  }

  @action.bound
  setPageNumberVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.PAGE_NUMBER, "visible", visible);
  }

  @action.bound
  setZoomControlsVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.ZOOM_CONTROL, "visible", visible);
  }

  @action.bound
  setFitWidthModeVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.FIT_WIDTH, "visible", visible);
  }

  @action.bound
  setFitPageModeVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.FIT_PAGE, "visible", visible);
  }

  @action.bound
  setFixedWidthVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.FIXED_WIDTH, "visible", visible);
  }

  @action.bound
  setPrintVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.PRINT, "visible", visible);
  }

  @action.bound
  setPageViewSwitcherVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.PAGE_VIEW_SWITCHER, "visible", visible);
  }

  @action.bound
  setViewWithScrollVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.VIEW_WITH_SCROLL, "visible", visible);
  }

  @action.bound
  setDownloadButtonVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.DOWNLOAD, "visible", visible);
  }

  @action.bound
  setTwoColumnButtonVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.TWO_COLUMN, "visible", visible);
  }

  setRotateViewButtonVisibility(visible) {
    setComponentProperty(this.componentProperties, appIdentifiers.ROTATE_VIEW_CONTROL, "visible", visible);
  }

  @action.bound
  customizeHudComponent(componentName, config) {
    if (
      !isZoomFeatureEnabled(this.stores) &&
      (componentName === appIdentifiers.ZOOM_CONTROL ||
        componentName === appIdentifiers.FIT_PAGE ||
        componentName === appIdentifiers.FIT_WIDTH) &&
      config.visible === true
    ) {
      return;
    }
    updateComponentProperties(this.componentProperties, componentName, config);
  }

  @action.bound
  setLeft(left) {
    this.left = left;
  }

  @action.bound
  setDockedWidth(dockedWidth) {
    this.dockedWidth = dockedWidth;
  }

  @action.bound
  setHUDWidth(hudWidth) {
    if (Math.abs(this.hudWidth - hudWidth) > 2) this.hudWidth = hudWidth;
  }

  @action.bound
  enableRotateControlInModernViewer(visible) {
    this.enableRotationInModernViewer = visible;
  }
}

export { HUDStore };
